import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { resetPassword, confirmResetPassword, signIn } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FORGOT_PASSWORD_PATH, LOGIN_PATH } from '@constants/routes';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import {
    PASSWORD_MIN_Length,
    PASSWORD_PATTERN,
    PASSWORD_TITLE,
    RESET_OTP_MAX_Length,
    RESET_OTP_PATTERN,
    RESET_OTP_TITLE,
    PASSWORD_MAX_Length
} from '@constants/form';
import ErrorMessage from '@components/Error/ErrorMessage';
import LoadingSpinner from '@components/Spinner';
import {
    loginSuccess,
    setPasswordChange,
} from '@store/reducers/auth/loginSlice';
import { userNewPassword } from '@utils/login';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { updatePassword } from 'aws-amplify/auth';

const ProfileResetUserPassword = () => {
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [confirmationCode, setConfirmationCode] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfrimPassword] =
        useState<boolean>(false);
    const [isClickable, setIsClickable] = useState<boolean>(false);
    const [countdown, setCountdown] = useState<number>(30);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const loginEmail = useAppSelector((state) => state.login.email);
    const newPassword = useAppSelector((state) => state.login.newPassword);
    const session = useAppSelector((state) => state.login.session);
    const timerRef = useRef<number | null>(null);

    const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
        const message = 'Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
    }, []);

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [loginEmail, navigate, handleBeforeUnload]);

    const getErrorMessage = (error: any): string => {
        switch (error.name) {
            case 'CodeMismatchException':
                return 'Invalid confirmation code.';
            case 'InvalidParameterException':
                return 'Invalid parameters provided.';
            case 'UserNotFoundException':
                return 'Invalid credentials.';
            case 'LimitExceededException':
                return 'Too many requests. Please try again later.';
            case 'NotAuthorizedException':
                return 'You are not authorized to perform this action.';
            case 'ExpiredCodeException':
                return 'Verification code expired.';
            default:
                return (
                    error.message ||
                    'An unknown error occurred during password reset'
                );
        }
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPassword(event.target.value);
        setError(null);
    };
    const handleCurrentPasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setCurrentPassword(event.target.value);
        setError(null);
    };
    const handleConfirmPasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setConfirmPassword(event.target.value);
        if (event.target.value !== password) {
            setError(t('auth.error.password_doest_not_match'));
        } else {
            setError(null);
        }
    };

    const handleToggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword((prevShowCurrentPassword) => !prevShowCurrentPassword);
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const handleToggleConfirmPasswordVisibility = () => {
        setShowConfrimPassword(
            (prevShowConfirmPassword) => !prevShowConfirmPassword
        );
    };

    const handleFormSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        try {
            setIsLoading(true);
            if (password !== confirmPassword) {
                setError(t('auth.error.password_doest_not_match'));
                setIsLoading(false);
                return;
            }

            await updatePassword({
                oldPassword: currentPassword,
                newPassword: password,
            });
            dispatch(setPasswordChange(true));
            navigate(LOGIN_PATH);
        } catch (error: any) {
            if (error.name === 'NotAuthorizedException') {
                setError('Current password is incorrect. Please try again.');
            } else {
                setError(getErrorMessage(error));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResetPassword = async () => {
        try {
            setIsLoading(true);
            if (
                password !== '' &&
                confirmPassword !== '' &&
                password !== confirmPassword
            ) {
                setError(t('auth.error.password_doest_not_match'));
                setIsLoading(false);
                return;
            }

            const output = await confirmResetPassword({
                username: loginEmail,
                confirmationCode: confirmationCode,
                newPassword: password,
            });

            dispatch(setPasswordChange(true));
            navigate(LOGIN_PATH);
            setIsLoading(false);
        } catch (error: any) {
            setError(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    };

    // const handleResendConfirmationCode = async () => {
    //     try {
    //         setIsLoading(true);
    //         if (isClickable) {
    //             startCountdown();
    //             setIsClickable(false);
    //             await resetPassword({ username: loginEmail });
    //             setIsLoading(false);
    //         }
    //     } catch (error: any) {
    //         setError(getErrorMessage(error));
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const handleBackClick = () => {
        navigate(-1);
    };

    return (
        <>
            <div
                className="flex font-bold text-base text-black items-center"
                onClick={handleBackClick}
            >
                <FaArrowLeftLong className="cursor-pointer" />{' '}
                <h2 className="font-bold ml-2 cursor-pointer">{t('back')}</h2>
            </div>
            <div className="flex flex-col items-center justify-center mt-4">
                <div className="card 2xl:w-[35%] lg:w-[60%] xl:w-[47%] border-none mb-3">
                    <div className="card-body px-5">
                        {!newPassword && (
                            <>
                                <h4 className="text-lg font-semibold text-nero my-2">
                                    {t('change_password')}
                                    {/* {t('auth.reset_password.title')} */}
                                </h4>
                            </>
                        )}
                        {error && <ErrorMessage message={error} />}
                        <form onSubmit={handleFormSubmit}>
                            {!newPassword && (
                                <div className="mt-3 mb-2">
                                    <label
                                        htmlFor="otp"
                                        className="mb-2 text-sm font-medium text-light_grey"
                                    >
                                        {t('current_password')}
                                        {/* {t('auth.reset_password.otp')} */}
                                    </label>
                                    <div className="relative flex items-center">
                                        <input
                                            type={
                                                showCurrentPassword
                                                    ? 'text'
                                                    : 'password'
                                            }
                                            id="current_password"
                                            className="form-control px-3 py-2 placeholder-gains_boro rounded-lg"
                                            name="current_password"
                                            value={currentPassword}
                                            onChange={
                                                handleCurrentPasswordChange
                                            }
                                            placeholder={t(
                                                'auth.reset_password.current_password_placeholder'
                                            )}
                                            minLength={PASSWORD_MIN_Length}
                                            maxLength={PASSWORD_MAX_Length}
                                            pattern={PASSWORD_PATTERN}
                                            required
                                            title={PASSWORD_TITLE}
                                            aria-describedby="password"
                                            autoComplete="new-password"
                                        />
                                        <span
                                            className="absolute right-3 cursor-pointer"
                                            onClick={
                                                handleToggleCurrentPasswordVisibility
                                            }
                                        >
                                            {showCurrentPassword ? (
                                                <IoEyeOutline className="text-secondary" />
                                            ) : (
                                                <IoEyeOffOutline className="text-secondary" />
                                            )}
                                        </span>
                                    </div>
                                </div>
                            )}
                            <h4 className="text-lg font-semibold text-oxford_blue mt-2">
                                {t('auth.reset_password.sub_title')}
                            </h4>
                            <div className="mt-2 form-password-toggle">
                                <div className="d-flex justify-content-between">
                                    <label
                                        className="mb-2 text-sm font-medium text-light_grey"
                                        htmlFor="password"
                                    >
                                        {t('new_password')}
                                    </label>
                                </div>
                                <div className="relative flex items-center">
                                    <input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        id="password"
                                        className="form-control px-3 py-2 placeholder-gains_boro rounded-lg"
                                        name="password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        placeholder={t(
                                            'auth.login.password_placeholder'
                                        )}
                                        minLength={PASSWORD_MIN_Length}
                                        maxLength={PASSWORD_MAX_Length}
                                        pattern={PASSWORD_PATTERN}
                                        required
                                        title={PASSWORD_TITLE}
                                        aria-describedby="password"
                                        autoComplete="new-password"
                                    />
                                    <span
                                        className="absolute right-3 cursor-pointer"
                                        onClick={handleTogglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <IoEyeOutline className="text-secondary" />
                                        ) : (
                                            <IoEyeOffOutline className="text-secondary" />
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-4 form-password-toggle">
                                <div className="d-flex justify-content-between">
                                    <label
                                        className="mb-2 text-sm font-medium text-light_grey"
                                        htmlFor="confirm_password"
                                    >
                                        {t(
                                            'auth.reset_password.confirm_password'
                                        )}
                                    </label>
                                </div>
                                <div className="relative flex items-center">
                                    <input
                                        type={
                                            showConfirmPassword
                                                ? 'text'
                                                : 'password'
                                        }
                                        id="confirm_password"
                                        className="form-control px-3 py-2 placeholder-gains_boro rounded-lg"
                                        name="confirm_password"
                                        value={confirmPassword}
                                        onChange={handleConfirmPasswordChange}
                                        placeholder={t(
                                            'auth.reset_password.confirm_password_placeholder'
                                        )}
                                        minLength={PASSWORD_MIN_Length}
                                        maxLength={PASSWORD_MAX_Length}
                                        pattern={PASSWORD_PATTERN}
                                        required
                                        title={PASSWORD_TITLE}
                                        aria-describedby="confirm_password"
                                        autoComplete="new-password"
                                    />
                                    <span
                                        className="absolute right-3 cursor-pointer"
                                        onClick={
                                            handleToggleConfirmPasswordVisibility
                                        }
                                    >
                                        {showConfirmPassword ? (
                                            <IoEyeOutline className="text-secondary" />
                                        ) : (
                                            <IoEyeOffOutline className="text-secondary" />
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-4 mb-3">
                                {isLoading ? (
                                    <div className="btn !bg-primary text-white text-base font-semibold py-2.5 w-full">
                                        <div>
                                            <LoadingSpinner className="text-secondary" />
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        className="btn !bg-primary text-white text-base font-semibold py-2.5 w-full"
                                        type="submit"
                                    >
                                        {t('auth.reset_password.submit')}
                                    </button>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProfileResetUserPassword;
